<template>
  <div class="complaint-container">
    <div class="page-content">
      <!-- 页面标题 -->
      <div class="page-title">{{ $t('complaint.list.title') }}</div>

      <!-- 状态筛选标签 -->
      <div class="status-tabs">
        <span
          v-for="item in complaintInfo.states"
          :key="item.statusCd"
          :class="['tab-item', { active: complaintInfo.conditions.state === item.statusCd }]"
          @click="swatchComplaintState(item)"
        >
          {{ item.name }}
        </span>
      </div>

      <!-- 搜索工具栏 -->
      <div class="search-toolbar">
        <div class="search-left">
          <el-select
            v-model="complaintInfo.conditions.typeCd"
            :placeholder="$t('complaint.search.type')"
            class="search-select"
            clearable
          >
            <el-option
              v-for="item in complaintInfo.complaintTypes"
              :key="item.typeCd"
              :label="item.typeName"
              :value="item.typeCd"
            />
          </el-select>
          <el-input
            v-model="complaintInfo.conditions.roomName"
            :placeholder="$t('complaint.search.room')"
            class="search-input"
            clearable
          />
          <el-input
            v-model="complaintInfo.conditions.complaintName"
            :placeholder="$t('complaint.search.contact')"
            class="search-input"
            clearable
          />
          <el-input
            v-model="complaintInfo.conditions.tel"
            :placeholder="$t('complaint.search.phone')"
            class="search-input"
            clearable
          />
          <el-input
            v-model="complaintInfo.conditions.complaintId"
            :placeholder="$t('complaint.search.id')"
            class="search-input"
            clearable
          />
          <el-date-picker
            v-model="complaintInfo.conditions.startTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            :placeholder="$t('complaint.search.startTime')"
            class="search-date"
            clearable
          />
          <el-date-picker
            v-model="complaintInfo.conditions.endTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            :placeholder="$t('complaint.search.endTime')"
            class="search-date"
            clearable
          />
        </div>
        <div class="search-right">
          <el-button class="search-btn" @click="_queryComplaintMethod">
            <i class="el-icon-search"></i>
            {{ $t('common.search') }}
          </el-button>
        </div>
      </div>

      <!-- 操作工具栏 -->
      <div class="toolbar">
        <el-button class="add-btn" @click="_openAddComplaintModal">
          <i class="el-icon-plus"></i>
          {{ $t('complaint.list.add') }}
        </el-button>
        <el-button class="icon-reset-btn" @click="handleReset">
          <i class="el-icon-refresh-right"></i>
        </el-button>
      </div>

      <!-- 列表 -->
      <el-table
        :data="complaintInfo.complaints"
        stripe
        class="data-table"
        v-loading="loading"
      >
        <el-table-column
          prop="communityName"
          :label="$t('complaint.table.community')"
          align="center"
          min-width="174"
        />
        <el-table-column
          prop="complaintId"
          :label="$t('complaint.table.id')"
          align="center"
          min-width="112"
        />
        <el-table-column
          prop="typeName"
          :label="$t('complaint.table.type')"
          align="center"
          min-width="182"
        />
        <el-table-column
          prop="roomName"
          :label="$t('complaint.table.room')"
          align="center"
          min-width="186"
        />
        <el-table-column
          prop="complaintName"
          :label="$t('complaint.table.contact')"
          align="center"
          min-width="122"
        />
        <el-table-column
          prop="tel"
          :label="$t('complaint.table.phone')"
          align="center"
          min-width="176"
        />
        <el-table-column
          prop="staffs"
          :label="$t('complaint.table.handler')"
          align="center"
          min-width="109"
        >
          <template slot-scope="scope">
            <span v-for="(item, index) in scope.row.staffs" :key="index">
              {{ item.staffName }}<br />
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="stateName"
          :label="$t('complaint.table.status')"
          align="center"
          min-width="122"
        >
          <template slot-scope="scope">
            <span
              :class="['status-tag', scope.row.state === 'W' ? 'status-processing' : 'status-completed']"
            >
              {{ scope.row.stateName }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="createTime"
          :label="$t('complaint.table.createTime')"
          align="center"
          min-width="221"
        />
        <el-table-column
          :label="$t('common.operation')"
          align="center"
          width="176"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button type="text" class="action-btn" @click="_openComplaintDetailModel(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
            <el-button type="text" class="action-btn" @click="_openEditComplaintModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button type="text" class="delete-btn" @click="_openDeleteComplaintModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        class="pagination-bar"
      />
    </div>

    <view-image ref="viewImage" />
    <edit-complaint ref="editComplaint" @success="handleSuccess" />
    <delete-complaint ref="deleteComplaint" @success="handleSuccess" />
    <add-complaint-modal ref="addComplaintModal" @success="handleSuccess" />
  </div>
</template>

<script>
import { listComplaints, listComplaintTypes } from '@/api/oa/complaintApi'
import { getEffectiveCommunityId, getMyEnteredCommunitys } from '@/api/community/communityApi'
import ViewImage from '@/components/system/viewImage'
import EditComplaint from '@/components/oa/editComplaint'
import DeleteComplaint from '@/components/oa/deleteComplaint'
import AddComplaintModal from '@/components/oa/AddComplaintModal'

export default {
  name: 'ComplaintList',
  components: {
    ViewImage,
    EditComplaint,
    DeleteComplaint,
    AddComplaintModal
  },
  data() {
    return {
      loading: false,
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      complaintInfo: {
        complaints: [],
        states: [
          { statusCd: '', name: this.$t('complaint.status.all') },
          { statusCd: 'W', name: this.$t('complaint.status.processing') },
          { statusCd: 'C', name: this.$t('complaint.status.completed') }
        ],
        complaintTypes: [],
        conditions: {
          complaintId: '',
          typeCd: '',
          complaintName: '',
          tel: '',
          roomName: '',
          state: '',
          startTime: '',
          endTime: '',
          communityId: ''
        }
      },
      authorizedCommunityIds: []
    }
  },
  async created() {
    this.communityId = getEffectiveCommunityId()
    this.complaintInfo.conditions.communityId = ''
    await this.loadAuthorizedCommunitys()
    this._listComplaintTypes()
    this._listComplaints()
  },
  methods: {
    async loadAuthorizedCommunitys() {
      try {
        const res = await getMyEnteredCommunitys({ page: 1, row: 1000 })
        this.authorizedCommunityIds = (res.communitys || [])
          .map(item => getEffectiveCommunityId(item.communityId))
          .filter(Boolean)
      } catch (error) {
        this.authorizedCommunityIds = []
      }
    },
    async _listComplaints() {
      try {
        this.loading = true
        const params = {
          ...this.complaintInfo.conditions,
          page: this.page.current,
          row: this.page.size
        }
        const { data, total } = await listComplaints(params)
        this.complaintInfo.complaints = data
        this.page.total = total
      } catch (error) {
        this.$message.error(this.$t('complaint.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async _listComplaintTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: ''
        }
        const { data } = await listComplaintTypes(params)
        this.complaintInfo.complaintTypes = data
      } catch (error) {
        console.error('获取投诉类型失败:', error)
      }
    },
    swatchComplaintState(item) {
      this.complaintInfo.conditions.state = item.statusCd
      this.page.current = 1
      this._listComplaints()
    },
    _openAddComplaintModal() {
      this.$refs.addComplaintModal.open()
    },
    _openEditComplaintModel(row) {
      this.$refs.editComplaint.open(row)
    },
    _openDeleteComplaintModel(row) {
      this.$refs.deleteComplaint.open(row)
    },
    _openComplaintDetailModel(row) {
      this.$router.push(`/pages/complaint/complaintDetail?complaintId=${row.complaintId}`)
    },
    _queryComplaintMethod() {
      this.page.current = 1
      this._listComplaints()
    },
    handleReset() {
      this.complaintInfo.conditions = {
        complaintId: '',
        typeCd: '',
        complaintName: '',
        tel: '',
        roomName: '',
        state: '',
        startTime: '',
        endTime: '',
        communityId: ''
      }
      this.page.current = 1
      this._listComplaints()
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listComplaints()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listComplaints()
    },
    handleSuccess() {
      this._listComplaints()
    }
  }
}
</script>

<style lang="scss" scoped>
.complaint-container {
  padding: 20px;
  background-color: #EBF2FA;
  min-height: calc(100vh - 84px);
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;

  .page-content {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 22px 24px;
  }

  .page-title {
    font-size: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 600;
    color: #1D2129;
    padding-bottom: 20px;
    border-bottom: 1px solid #DCE6F5;
    margin-bottom: 20px;
    text-align: left;
  }

  .status-tabs {
    display: flex;
    align-items: center;
    gap: 40px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #EBF2FA;

    .tab-item {
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      font-weight: 600;
      color: #1D2129;
      cursor: pointer;
      position: relative;
      padding-bottom: 12px;
      margin-bottom: -13px;

      &.active {
        color: #0E76FF;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #0E76FF;
          border-radius: 1px;
        }
      }

      &:hover {
        color: #0E76FF;
      }
    }
  }

  .search-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;

    .search-left {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;

      .search-select,
      .search-input {
        width: 200px;

        ::v-deep .el-input__inner {
          border-radius: 10px;
          border: 1px solid #DCE6F5;
          background: #FFFFFF;
          font-family: 'Inter', 'PingFang SC', sans-serif;
          font-size: 14px;
          color: #1D2129;
          height: 38px;
          line-height: 38px;

          &::placeholder {
            color: #8C9BB4;
          }

          &:focus {
            border-color: #0E76FF;
          }
        }
      }

      .search-date {
        width: 190px;

        ::v-deep .el-input__inner {
          border-radius: 10px;
          border: 1px solid #DCE6F5;
          background: #FFFFFF;
          font-family: 'Inter', 'PingFang SC', sans-serif;
          font-size: 14px;
          color: #1D2129;
          height: 38px;
          line-height: 38px;

          &::placeholder {
            color: #8C9BB4;
          }

          &:focus {
            border-color: #0E76FF;
          }
        }
      }
    }

    .search-right {
      .search-btn {
        width: 84px;
        height: 38px;
        border-radius: 10px;
        background: #FFFFFF;
        border: 1px solid #0E76FF;
        color: #0E76FF;
        font-family: 'Inter', 'PingFang SC', sans-serif;
        font-weight: 500;
        font-size: 14px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        &:hover {
          background: #E8F3FF;
        }

        i {
          font-size: 12px;
        }
      }
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;

    .add-btn {
      width: 113px;
      height: 38px;
      border-radius: 10px;
      background: #0E76FF;
      border: none;
      color: #FFFFFF;
      font-family: 'Inter', 'PingFang SC', sans-serif;
      font-weight: 500;
      font-size: 14px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;

      &:hover {
        background: #1C99FF;
      }

      i {
        font-size: 12px;
      }
    }

    .icon-reset-btn {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      border: 1px solid #DCE6F5;
      background: #FFFFFF;
      color: #8C9BB4;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 14px;
      }

      &:hover {
        color: #0E76FF;
        border-color: #0E76FF;
      }
    }
  }

  .data-table {
    border-radius: 10px;
    overflow: hidden;

    &::before {
      display: none;
    }

    ::v-deep .el-table__header-wrapper {
      th {
        background-color: #EBF2FA !important;
        color: #364153 !important;
        font-weight: 500;
        font-size: 14px;
        font-family: 'PingFang SC', sans-serif;
        padding: 12px 0;
        height: 50px;
      }
    }

    ::v-deep .el-table__row--striped td {
      background-color: #F8FBFF !important;
    }

    ::v-deep .el-table__body tr:hover > td {
      background-color: #E8F3FF !important;
    }

    ::v-deep td {
      color: #1D2129;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      padding: 12px 0;
      height: 50px;
    }

    .status-tag {
      display: inline-block;
      padding: 3px 12px;
      border-radius: 8px;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      font-weight: 400;
    }

    .status-processing {
      background: rgba(255, 105, 0, 0.10);
      color: #FF6900;
      border: 1px solid #FF6900;
    }

    .status-completed {
      background: rgba(140, 155, 180, 0.10);
      color: #8C9BB4;
      border: 1px solid #8C9BB4;
    }

    ::v-deep .action-btn,
    ::v-deep .delete-btn {
      border-radius: 0;
      background: transparent;
      border: none;
      padding: 0 8px;
      color: #0E76FF;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      font-weight: 500;

      &:hover {
        color: #1C99FF;
        background: transparent;
      }
    }

    ::v-deep .delete-btn {
      color: #FB2C36;

      &:hover {
        color: #ff4d4f;
      }
    }
  }

  .pagination-bar {
    margin-top: 20px;
    text-align: right;

    ::v-deep .el-pagination__total,
    ::v-deep .el-pagination__jump {
      color: #1D2129;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
    }

    ::v-deep .el-pager li {
      border-radius: 2px;
      width: 32px;
      height: 32px;
      line-height: 32px;

      &.active {
        background-color: #E8F3FF !important;
        color: #0E76FF !important;
        border-radius: 8px;
        font-family: 'Nunito Sans', sans-serif;
        font-weight: 600;
      }
    }

    ::v-deep .el-pagination__sizes .el-input__inner {
      background: #F2F3F5;
      border-radius: 8px;
      border: none;
      height: 32px;
      line-height: 32px;
    }

    ::v-deep .btn-prev,
    ::v-deep .btn-next {
      width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 2px;
    }
  }
}
</style>
